import { useEffect, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Clue, GameRound } from "../types";
import "./JeopardyBoard.css";

const DD_SECONDS = 5;

interface JeopardyBoardProps {
  backToBoard: () => void;
  board: GameRound;
  categoryShown: () => void;
  categoriesShown: number;
  chooseClue: (categoryIndex: number, clueIndex: number) => void;
  currentCategory: number | null;
  currentClue: number | null;
  solutionRevealed?: boolean;
  ddShown?: boolean;
  ddWager?: { teamIndex: number; amount: number } | null;
}

function JeopardyBoard(props: JeopardyBoardProps) {
  const {
    backToBoard,
    board,
    categoryShown,
    categoriesShown,
    chooseClue,
    currentCategory,
    currentClue,
    solutionRevealed = false,
    ddShown = false,
    ddWager = null,
  } = props;

  // Population phase — when the board first appears, cells fade in with
  // their $values in random order over ~3.5s before the category reveals start.
  const totalCells = board.length * board[0].clues.length;
  const [populating, setPopulating] = useState(true);
  const [valuesShown, setValuesShown] = useState<boolean[]>(() => new Array(totalCells).fill(false));

  useEffect(() => {
    const order = Array.from({ length: totalCells }, (_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    order.forEach((idx, i) => {
      timers.push(setTimeout(() => {
        setValuesShown(prev => {
          if (prev[idx]) return prev;
          const next = [...prev];
          next[idx] = true;
          return next;
        });
      }, i * 110));
    });
    // Hand off to category reveal a beat after the last cell appears.
    timers.push(setTimeout(() => setPopulating(false), totalCells * 110 + 600));
    return () => timers.forEach(clearTimeout);
  }, [totalCells]);

  // DD countdown timer — starts when ddShown becomes true
  const [ddTimeLeft, setDdTimeLeft] = useState<number | null>(null);
  const ddTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevDdShown = useRef(false);

  useEffect(() => {
    if (ddShown && !prevDdShown.current) {
      // DD question just revealed — start 30s countdown
      setDdTimeLeft(DD_SECONDS);
      if (ddTimerRef.current) clearInterval(ddTimerRef.current);
      ddTimerRef.current = setInterval(() => {
        setDdTimeLeft(t => {
          if (t === null || t <= 1) {
            clearInterval(ddTimerRef.current!);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    if (!ddShown) {
      if (ddTimerRef.current) clearInterval(ddTimerRef.current);
      setDdTimeLeft(null);
    }
    prevDdShown.current = ddShown;
    return () => { if (ddTimerRef.current) clearInterval(ddTimerRef.current); };
  }, [ddShown]);

  useEffect(() => {
    document.addEventListener("keydown", clueKeyPress);
    return () => {
      document.removeEventListener("keydown", clueKeyPress);
    };
  });

  // Auto-advance category reveals — each category shows for 1.3s then scrolls to next
  const categoryShownRef = useRef(categoryShown);
  categoryShownRef.current = categoryShown;
  useEffect(() => {
    if (categoriesShown >= board.length) return;
    // 2.2s per category gives the host time to actually read each name as
    // the visual reveal slides through.
    const timer = setTimeout(() => categoryShownRef.current(), 2200);
    return () => clearTimeout(timer);
  }, [categoriesShown, board.length]);

  function renderCategory(index: number) {
    return (
      <div className="category-container">
        <TransitionGroup>
          <CSSTransition key={index} timeout={1000} classNames="categorybox">
            <div className="category-box">
              <div className="category">{board[index].category}</div>
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
    );
  }

  function renderClue(categoryName: string, clue: Clue, value: number) {
    const showDailyDoubleScreen = clue.dailyDouble && !ddShown;
    return (
      <>
        <div
          className={`clue${showDailyDoubleScreen ? " clue-dd-screen" : ""}`}
          style={{ cursor: 'default' }}
        >
          <div className="clue-category-label">
            {categoryName}{clue.dailyDouble ? '' : ` - $${clue.value}`}
          </div>
          <div
            className={[
              "clue-display",
              showDailyDoubleScreen ? "daily-double" : "",
              solutionRevealed ? "clue-display-answer" : "",
            ].join(" ").trim()}
          >
            {showDailyDoubleScreen ? (
              <span className="dd-text" key="dd">Daily Double</span>
            ) : (
              <>
                {clue.dailyDouble && ddWager && !solutionRevealed && (
                  <div className="dd-wager-display">
                    <span className="dd-wager-label">WAGER</span>
                    <span className="dd-wager-amount">${ddWager.amount.toLocaleString()}</span>
                  </div>
                )}
                <span className="clue-content" key={solutionRevealed ? 'revealed' : 'clue'}>
                  {clue.html === true ? (
                    <span dangerouslySetInnerHTML={{ __html: solutionRevealed ? clue.solution : clue.clue }} />
                  ) : solutionRevealed ? clue.solution : clue.clue}
                </span>
              </>
            )}
          </div>
        </div>
        {/* Timer below clue — shown for DD after question revealed */}
        {clue.dailyDouble && !solutionRevealed && ddTimeLeft !== null && (
          <div className={`clue-bottom-timer ${ddTimeLeft <= 10 ? 'clue-bottom-timer-urgent' : ''}`}>
            {ddTimeLeft}
          </div>
        )}
      </>
    );
  }

  function clueKeyPress(event: KeyboardEvent) {
    if (event.key === "Escape") {
      returnToBoard();
    }
  }

  function returnToBoard() {
    backToBoard();
  }

  // Population phase: render the table with cells animated in, no category names yet.
  if (populating && currentCategory === null && currentClue === null) {
    return (
      <div className="board-table-wrap">
        <table>
          <thead>
            <tr>
              {board.map((_, i) => (
                <td key={i} className="category-title category-title-placeholder">?</td>
              ))}
            </tr>
          </thead>
          <tbody>
            {board[0].clues.map((_, j) => (
              <tr key={j}>
                {board.map((category, i) => {
                  const idx = i * board[0].clues.length + j;
                  const shown = valuesShown[idx];
                  return (
                    <td
                      key={i}
                      className={`board-clue board-clue-display board-clue-populate ${shown ? 'shown' : ''}`}
                    >
                      ${category.clues[j].value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // First check for if we need to present categories
  if (categoriesShown < board.length) {
    return renderCategory(categoriesShown);
  }

  // Check if there is a clue to present
  if (currentCategory !== null && currentClue !== null) {
    return renderClue(
      board[currentCategory].category,
      board[currentCategory].clues[currentClue],
      board[currentCategory].clues[currentClue].value
    );
  }

  return (
    <div className="board-table-wrap">
      <table>
        <thead>
          <tr>
            {board.map((category, i) => (
              <td key={i} className="category-title">
                {category.category}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {board[0].clues.map((_, j) => {
            return (
              <tr key={j}>
                {board.map((category, i) => {
                  if (category.clues[j].chosen) {
                    return <td key={i} className="board-clue"></td>;
                  }
                  return (
                    <td key={i} className="board-clue board-clue-display">
                      ${category.clues[j].value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default JeopardyBoard;
